import BadgeInput from "@components/ui/badge-input";
import { Button } from "@components/ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "@components/ui/input-group";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { SearchIcon, ArrowRightIcon } from "lucide-react";
import { Controller, SubmitHandler } from "react-hook-form";
import { useFormCompat } from "@/hooks/useFormCompat";
import {
  SearchIndexForm as IndexFormType,
  useSearchIndexFormSchema as useSearchIndexFormSchema,
} from "./types";
import { useSearchContext } from "../context";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { ROUTES } from "@/routes_new";
import { capitalize, formatDateForApi } from "@/utils";
import { SearchTimePeriod, usePredefinedTimeIntervals } from "./helpers";

export default function IndexForm() {
  const { t } = useLingui();
  const history = useHistory();

  const searchIndexFormSchema = useSearchIndexFormSchema();
  const predefinedTimeIntervals = usePredefinedTimeIntervals();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Context
  const { defaultQuery, setCandidateSearch, setPage } = useSearchContext();

  // Form state
  const form = useFormCompat<IndexFormType>({
    resolver: zodResolver(searchIndexFormSchema),
    defaultValues: {
      keywords: [],
      nearKeywords: [],
      notNearKeywords: [],
    },
  });

  // Handlers
  const handleSubmit: SubmitHandler<IndexFormType> = function (values) {
    const timePeriod = predefinedTimeIntervals.find(
      (pdt) => pdt.value.id === SearchTimePeriod.Last30Days
    );

    if (!timePeriod) {
      return;
    }

    setCandidateSearch({
      title: values.keywords[0] ? capitalize(values.keywords[0]) : undefined,
      start_date: formatDateForApi(timePeriod.value.date),
      queries: [
        {
          ...defaultQuery,
          keywords: values.keywords,
          near_keywords: values.nearKeywords,
          not_near_keywords: values.notNearKeywords,
        },
      ],
    });

    setPage(1);

    history.push(
      generatePath(ROUTES.SEARCH_PREVIEW_ORGID, {
        orgUuid,
      })
    );
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="w-full">
      <FieldGroup className="gap-6">
        <Controller
          name="keywords"
          control={form.control}
          render={({ field, fieldState }) => {
            const id = "keywords";

            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel className="sr-only" htmlFor={id}>
                  <Trans>Sökord</Trans>
                </FieldLabel>

                <InputGroup className="h-auto">
                  <InputGroupAddon>
                    <SearchIcon className="-translate-y-[0.5px]" />
                  </InputGroupAddon>
                  <BadgeInput
                    withBorder={false}
                    value={field.value || []}
                    onChange={field.onChange}
                    inputProps={
                      {
                        placeholder: field.value?.length
                          ? undefined
                          : t`Ange ett företag, ämne, person eller sökord (All Ears*, allears)`,
                        id,
                        "data-testid": "input-keywords",
                        "aria-invalid": fieldState.invalid,
                        autoComplete: "off",
                        autoFocus: true,
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupText>⏎</InputGroupText>
                  </InputGroupAddon>
                </InputGroup>

                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <div className="flex gap-3">
          <Controller
            name="nearKeywords"
            control={form.control}
            render={({ field, fieldState }) => {
              const id = "nearKeywords";

              return (
                <Field data-invalid={fieldState.invalid} className="flex-1">
                  <FieldLabel className="sr-only" htmlFor={id}>
                    <Trans>Sambandsord</Trans>
                  </FieldLabel>

                  <InputGroup className="h-auto">
                    <BadgeInput
                      variant="cyan"
                      withBorder={false}
                      value={field.value || []}
                      onChange={field.onChange}
                      inputProps={{
                        placeholder: field.value?.length
                          ? undefined
                          : t`Sambandsord (valfritt)`,
                        id,
                        "aria-invalid": fieldState.invalid,
                        autoComplete: "off",
                      }}
                    />
                    <InputGroupAddon align="inline-end">
                      <InputGroupText>⏎</InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>

                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              );
            }}
          />

          <Controller
            name="notNearKeywords"
            control={form.control}
            render={({ field, fieldState }) => {
              const id = "notNearKeywords";

              return (
                <Field data-invalid={fieldState.invalid} className="flex-1">
                  <FieldLabel className="sr-only" htmlFor={id}>
                    <Trans>Exkludera ord</Trans>
                  </FieldLabel>

                  <InputGroup className="h-auto">
                    <BadgeInput
                      variant="red"
                      withBorder={false}
                      value={field.value || []}
                      onChange={field.onChange}
                      inputProps={{
                        placeholder: field.value?.length
                          ? undefined
                          : t`Exludera ord (valfritt)`,
                        id,
                        "aria-invalid": fieldState.invalid,
                        autoComplete: "off",
                      }}
                    />
                    <InputGroupAddon align="inline-end">
                      <InputGroupText>⏎</InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>

                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              );
            }}
          />

          <Button
            type="submit"
            id="form-qb-preview"
            data-testid="search-button"
          >
            <Trans>Förhandsvisa</Trans>
            <ArrowRightIcon />
          </Button>
        </div>
      </FieldGroup>
    </form>
  );
}
